/**
 * Profile Service
 * 
 * Service for managing user profiles using Supabase client
 * Following the service pattern from categorisation feature
 */

import { supabase } from '../lib/supabase';
import { getCurrentUserId } from '../lib/supabase';
import type {
  Profile,
  UpdateProfileInput,
  ExportDataOptions,
  DeleteAccountInput,
  ProfileResponse,
  ExportDataResponse,
  SuccessResponse
} from '../types/profile.types';
import { validateUpdateProfileInput } from '../utils/profile-validation';

export interface ProfileServiceResponse<T> {
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

// Helper function to convert database row to Profile
function convertDbRowToProfile(row: any, authUser: any): Profile {
  return {
    id: row.id,
    userId: row.user_id,
    email: authUser?.email || '',
    emailConfirmed: !!authUser?.email_confirmed_at,
    firstName: row.first_name || undefined,
    lastName: row.last_name || undefined,
    phoneNumber: row.phone_number || undefined,
    dateOfBirth: row.date_of_birth ? new Date(row.date_of_birth).toISOString().split('T')[0] : undefined,
    countryOfResidence: row.country_of_residence || 'Australia',
    profileImageUrl: row.profile_image_url || undefined,
    accountStatus: row.account_status || 'active',
    membershipType: row.membership_type || 'free',
    role: row.role || 'user',
    passwordLastChanged: row.password_last_changed || new Date().toISOString(),
    createdAt: row.created_at || new Date().toISOString(),
    updatedAt: row.updated_at || new Date().toISOString()
  };
}

// Helper function to convert UpdateProfileInput to database format
function convertUpdateInputToDb(input: UpdateProfileInput): any {
  const dbInput: any = {};
  
  if (input.firstName !== undefined) {
    dbInput.first_name = input.firstName || null;
  }
  if (input.lastName !== undefined) {
    dbInput.last_name = input.lastName || null;
  }
  if (input.phoneNumber !== undefined) {
    dbInput.phone_number = input.phoneNumber || null;
  }
  if (input.dateOfBirth !== undefined) {
    dbInput.date_of_birth = input.dateOfBirth || null;
  }
  if (input.countryOfResidence !== undefined) {
    dbInput.country_of_residence = input.countryOfResidence;
  }
  
  dbInput.updated_at = new Date().toISOString();
  
  return dbInput;
}

export const profileService = {
  /**
   * Get current user's profile
   */
  async getProfile(): Promise<ProfileResponse> {
    try {
      const userId = await getCurrentUserId();
      
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User is not authenticated'
          }
        };
      }

      // Get profile from user_profiles table
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (profileError) {
        if (profileError.code === 'PGRST116') {
          return {
            error: {
              code: 'NOT_FOUND',
              message: 'Profile not found'
            }
          };
        }
        return {
          error: {
            code: 'UNKNOWN_ERROR',
            message: profileError.message || 'Failed to fetch profile'
          }
        };
      }

      // Get auth user for email
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();

      if (authError || !authUser) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'Failed to get authenticated user'
          }
        };
      }

      const profile = convertDbRowToProfile(profileData, authUser);

      return {
        data: profile
      };
    } catch (error: any) {
      console.error('Error getting profile:', error);
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'Failed to get profile'
        }
      };
    }
  },

  /**
   * Update current user's profile
   */
  async updateProfile(input: UpdateProfileInput): Promise<ProfileResponse> {
    try {
      const userId = await getCurrentUserId();
      
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User is not authenticated'
          }
        };
      }

      // Validate input
      const validation = validateUpdateProfileInput(input);
      if (!validation.valid && validation.errors) {
        const firstError = validation.errors.issues[0];
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: firstError.message || 'Validation failed',
            details: validation.errors.issues
          }
        };
      }

      // Convert input to database format
      const dbInput = convertUpdateInputToDb(input);

      // Update profile
      const { data: updatedProfile, error: updateError } = await supabase
        .from('user_profiles')
        .update(dbInput)
        .eq('user_id', userId)
        .select()
        .single();

      if (updateError) {
        if (updateError.code === 'PGRST301') {
          return {
            error: {
              code: 'PERMISSION_ERROR',
              message: 'Permission denied'
            }
          };
        }
        return {
          error: {
            code: 'UNKNOWN_ERROR',
            message: updateError.message || 'Failed to update profile'
          }
        };
      }

      // Get auth user for email
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();

      if (authError || !authUser) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'Failed to get authenticated user'
          }
        };
      }

      const profile = convertDbRowToProfile(updatedProfile, authUser);

      return {
        data: profile
      };
    } catch (error: any) {
      console.error('Error updating profile:', error);
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'Failed to update profile'
        }
      };
    }
  },

  /**
   * Export user data in JSON or CSV format
   */
  async exportData(options: ExportDataOptions): Promise<ExportDataResponse> {
    try {
      const userId = await getCurrentUserId();
      
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User is not authenticated'
          }
        };
      }

      // Fetch all user data
      const [profileResult, accountsResult, transactionsResult, categoriesResult, tagsResult] = await Promise.all([
        supabase.from('user_profiles').select('*').eq('user_id', userId).single(),
        supabase.from('accounts').select('*').eq('user_id', userId),
        supabase.from('transactions').select('*').eq('user_id', userId).order('date', { ascending: false }),
        supabase.from('categories').select('*').eq('user_id', userId).or('is_system.eq.true'),
        supabase.from('transaction_tags').select('*').eq('user_id', userId)
      ]);

      // Collect all data
      const exportData = {
        profile: profileResult.data || null,
        accounts: accountsResult.data || [],
        transactions: transactionsResult.data || [],
        categories: categoriesResult.data || [],
        tags: tagsResult.data || [],
        exportedAt: new Date().toISOString()
      };

      let content: string;
      let mimeType: string;
      let filename: string;

      if (options.format === 'json') {
        content = JSON.stringify(exportData, null, 2);
        mimeType = 'application/json';
        filename = `moneymojo-export-${new Date().toISOString().split('T')[0]}.json`;
      } else {
        // CSV format - simplified, just export profile for now
        const csvRows: string[] = [];
        csvRows.push('Type,Field,Value');
        if (exportData.profile) {
          Object.entries(exportData.profile).forEach(([key, value]) => {
            csvRows.push(`Profile,${key},"${value}"`);
          });
        }
        content = csvRows.join('\n');
        mimeType = 'text/csv';
        filename = `moneymojo-export-${new Date().toISOString().split('T')[0]}.csv`;
      }

      // Create blob and download URL
      const blob = new Blob([content], { type: mimeType });
      const downloadUrl = URL.createObjectURL(blob);

      // Trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(downloadUrl);
      }, 100);

      return {
        data: {
          downloadUrl,
          format: options.format,
          size: blob.size
        }
      };
    } catch (error: any) {
      console.error('Error exporting data:', error);
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'Failed to export data'
        }
      };
    }
  },

  /**
   * Delete user account (soft delete with 30-day recovery window)
   * 
   * This performs a soft delete by:
   * 1. Adding the account to account_deletion_queue
   * 2. Setting deleted_at to now
   * 3. Setting deletion_scheduled_at to deleted_at + 30 days
   * 4. Signing out the user
   * 
   * The account can be recovered within 30 days.
   * After 30 days, a scheduled job will permanently delete the account.
   */
  async deleteAccount(input: DeleteAccountInput): Promise<SuccessResponse> {
    try {
      const userId = await getCurrentUserId();
      
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User is not authenticated'
          }
        };
      }

      // Validate confirmation
      if (input.confirmation !== 'DELETE') {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Confirmation must be exactly "DELETE"'
          }
        };
      }

      // Calculate deletion scheduled date (30 days from now)
      const deletedAt = new Date();
      const deletionScheduledAt = new Date(deletedAt);
      deletionScheduledAt.setDate(deletionScheduledAt.getDate() + 30);

      // Insert into account_deletion_queue (soft delete)
      const { error: queueError } = await supabase
        .from('account_deletion_queue')
        .insert({
          user_id: userId,
          deleted_at: deletedAt.toISOString(),
          deletion_scheduled_at: deletionScheduledAt.toISOString(),
        })
        .select()
        .single();

      if (queueError) {
        // If record already exists, update it
        if (queueError.code === '23505') { // Unique violation
          const { error: updateError } = await supabase
            .from('account_deletion_queue')
            .update({
              deleted_at: deletedAt.toISOString(),
              deletion_scheduled_at: deletionScheduledAt.toISOString(),
              recovered_at: null, // Clear recovery if it was set
            })
            .eq('user_id', userId);

          if (updateError) {
            return {
              error: {
                code: 'UNKNOWN_ERROR',
                message: updateError.message || 'Failed to queue account deletion'
              }
            };
          }
        } else {
          return {
            error: {
              code: 'UNKNOWN_ERROR',
              message: queueError.message || 'Failed to queue account deletion'
            }
          };
        }
      }

      // Sign out user
      await supabase.auth.signOut();

      return {
        data: {
          success: true
        }
      };
    } catch (error: any) {
      console.error('Error deleting account:', error);
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'Failed to delete account'
        }
      };
    }
  }
};

